import React, { useContext, useMemo } from 'react';

import BaseViewsContext from 'contexts/BaseViewsContext';
import DatasetDefinitionsContext from 'contexts/DatasetDefinitionsContext';
import useGetFieldLabel from 'hooks/useGetFieldLabel';
import FormInput from 'kingpin/forms/FormInput';
import Inputs from 'components/Inputs';

import { HIDE_HEADER_TOTAL } from '../headerTotalField';

/**
 * Optional override for the field summed in a sub-period summary section's header
 * total. Defaults to the reward's own value field (points); pick e.g.
 * `safetyBonusPay` to show the dollar twin instead — formatting follows the field's
 * own base-view type — or "None" to hide the total. Offers the program dataset's
 * reward/pay fields (names ending in `Pay`/`Points`, excluding potential twins),
 * narrowed to the selected reward when one is given. Renders nothing until a program
 * is chosen.
 */
const HeaderTotalFieldInput = ({
  bonusConfigId,
  rewardName,
  value,
  setValue,
}: {
  bonusConfigId: string | undefined;
  // The section's reward (Bonus Reward section) — narrows the field list to fields
  // for that reward. Omitted for the Bonus Total Reward section (whole list shown).
  rewardName?: string;
  value: string | undefined;
  setValue: (field: string | undefined) => void;
}) => {
  const { bonusPrograms } = useContext(DatasetDefinitionsContext);
  const { baseViews } = useContext(BaseViewsContext);
  const { getFieldLabel } = useGetFieldLabel();

  const dataset = useMemo(
    () =>
      bonusPrograms.find((program) => program.id === bonusConfigId)?.dataset,
    [bonusPrograms, bonusConfigId],
  );

  const fieldOptions = useMemo((): string[] => {
    if (!dataset) {
      return [];
    }
    // Reward/pay actuals only — drop the `*Potential*` twins that clutter the list.
    const payAndPointsFields = Object.keys(baseViews[dataset]?.fields ?? {})
      .filter(
        (field) => /(Pay|Points)$/.test(field) && !/potential/i.test(field),
      )
      .sort();

    if (!rewardName) {
      return payAndPointsFields;
    }
    // Narrow to fields for the selected reward (e.g. `safety` → `safetyBonusPay`,
    // `safetyPoints`). Fall back to the full list if naming doesn't line up, so the
    // picker never ends up empty.
    const forReward = payAndPointsFields.filter((field) =>
      field.toLowerCase().startsWith(rewardName.toLowerCase()),
    );
    return forReward.length > 0 ? forReward : payAndPointsFields;
  }, [baseViews, dataset, rewardName]);

  const options = useMemo((): DropdownOption[] => {
    if (!dataset) {
      return [];
    }
    return [
      {
        key: undefined,
        label: 'Reward default',
        isSelected: value === undefined,
        onSelected: () => setValue(undefined),
      },
      {
        key: HIDE_HEADER_TOTAL,
        label: 'None',
        isSelected: value === HIDE_HEADER_TOTAL,
        onSelected: () => setValue(HIDE_HEADER_TOTAL),
      },
      ...fieldOptions.map((field) => ({
        key: field,
        label: getFieldLabel({ field, dataType: dataset }),
        isSelected: value === field,
        onSelected: () => setValue(field),
      })),
    ];
  }, [dataset, fieldOptions, getFieldLabel, setValue, value]);

  if (!dataset) {
    return null;
  }

  return (
    <FormInput label={'Header total field'}>
      <Inputs.Dropdown options={options} />
    </FormInput>
  );
};

export default HeaderTotalFieldInput;
